// components/admin/BackupTab.js
// כרטיסיית גיבויים — CompanySettings Tab 2
//
// API:
//   GET /backup/list → [{filename, size, created_at}, ...] (מערך ישיר)
//   POST /backup/create → יצירת גיבוי
//   POST /backup/restore { filename } → שחזור
//   POST /backup/upload (multipart, field: backup) → העלאה (.sql, max 100MB)
//   GET /backup/download/{filename} → הורדה (blob)
//   DELETE /backup/{filename} → מחיקה
//
// Restore flow (frontend logic — 2 separate API calls):
//   "גבה ושחזר": POST /backup/create → await → POST /backup/restore { filename }
//   "שחזר ללא גיבוי": POST /backup/restore { filename } → reload page

import { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  IconButton,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BackupIcon from '@mui/icons-material/BackupOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';
import api, { uploadFile } from '../../services/api';

// =============================================================================
// BackupTab
// =============================================================================
export default function BackupTab({ showSnackbar }) {
  const { t } = useTranslation();

  // Backup list
  const { data: backups, loading, error, refetch } = useApi('/backup/list', {
    defaultData: [],
  });

  const { mutate, loading: mutating } = useApiMutation();

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Restore dialog state
  const [restoreFile, setRestoreFile] = useState(null); // filename to restore
  const [restoring, setRestoring] = useState(false);
  const [restoreStep, setRestoreStep] = useState(''); // '' | 'backing-up' | 'restoring'

  // Delete confirm state
  const [deleteFile, setDeleteFile] = useState(null);

  // ===================================================================
  // Create backup
  // ===================================================================
  const handleCreateBackup = useCallback(async () => {
    if (mutating) return;
    const result = await mutate('post', '/backup/create');
    if (result.success) {
      showSnackbar(t('companySettings.backup.backupCreated'));
      refetch();
    } else {
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [mutating, mutate, refetch, showSnackbar, t]);

  // ===================================================================
  // Download backup
  // ===================================================================
  const handleDownload = useCallback(async (filename) => {
    try {
      const response = await api.get(`/backup/download/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/sql' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || t('common.error'), 'error');
    }
  }, [showSnackbar, t]);

  // ===================================================================
  // Restore — complex flow
  // ===================================================================
  const handleOpenRestore = useCallback((filename) => {
    setRestoreFile(filename);
    setRestoreStep('');
    setRestoring(false);
  }, []);

  // Restore WITHOUT backup first
  const handleRestoreWithout = useCallback(async () => {
    if (restoring || !restoreFile) return;
    setRestoring(true);
    setRestoreStep('restoring');

    const result = await mutate('post', '/backup/restore', { filename: restoreFile });

    if (result.success) {
      showSnackbar(t('companySettings.backup.restoreSuccess'));
      // Reload page after restore
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setRestoring(false);
      setRestoreStep('');
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [restoring, restoreFile, mutate, showSnackbar, t]);

  // Restore WITH backup first (2 API calls)
  const handleRestoreWithBackup = useCallback(async () => {
    if (restoring || !restoreFile) return;
    setRestoring(true);

    // Step 1: Create backup
    setRestoreStep('backing-up');
    const createResult = await mutate('post', '/backup/create');
    if (!createResult.success) {
      setRestoring(false);
      setRestoreStep('');
      showSnackbar(createResult.error || t('common.error'), 'error');
      return;
    }

    // Step 2: Restore
    setRestoreStep('restoring');
    const restoreResult = await mutate('post', '/backup/restore', { filename: restoreFile });
    if (restoreResult.success) {
      showSnackbar(t('companySettings.backup.restoreSuccess'));
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setRestoring(false);
      setRestoreStep('');
      showSnackbar(restoreResult.error || t('common.error'), 'error');
    }
  }, [restoring, restoreFile, mutate, showSnackbar, t]);

  // ===================================================================
  // Upload backup file
  // ===================================================================
  const handleUploadFile = useCallback(async (file) => {
    if (!file || uploading) return;

    // Validate extension
    if (!file.name.toLowerCase().endsWith('.sql')) {
      showSnackbar(t('companySettings.backup.uploadHelp'), 'error');
      return;
    }

    // Validate size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      showSnackbar(t('companySettings.backup.uploadHelp'), 'error');
      return;
    }

    setUploading(true);
    try {
      await uploadFile('/backup/upload', file, 'backup');
      showSnackbar(t('companySettings.backup.backupCreated'));
      refetch();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || t('common.error');
      showSnackbar(msg, 'error');
    } finally {
      setUploading(false);
    }
  }, [uploading, refetch, showSnackbar, t]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleUploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleUploadFile]);

  // Drag & drop
  const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUploadFile(file);
  }, [handleUploadFile]);

  // ===================================================================
  // Delete backup
  // ===================================================================
  const handleDelete = useCallback(async () => {
    if (!deleteFile || mutating) return;
    const result = await mutate('delete', `/backup/${encodeURIComponent(deleteFile)}`);
    setDeleteFile(null);
    if (result.success) {
      showSnackbar(t('common.success'));
      refetch();
    } else {
      showSnackbar(result.error || t('common.error'), 'error');
    }
  }, [deleteFile, mutating, mutate, refetch, showSnackbar, t]);

  // ===================================================================
  // Format helpers
  // ===================================================================
  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const isBusy = mutating || uploading || restoring;

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="settings__backup-tab">
      {(loading || isBusy) && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button
          data-testid="settings__backup__button--create"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateBackup}
          disabled={isBusy}
        >
          {t('companySettings.backup.createBackup')}
        </Button>
        <Button
          data-testid="settings__backup__button--upload"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
        >
          {t('companySettings.backup.uploadBackup')}
        </Button>
      </Box>

      {/* Backup list table */}
      {(!backups || backups.length === 0) && !loading ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <BackupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {t('companySettings.backup.noBackups')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="settings__backup__table">
            <TableHead>
              <TableRow>
                <TableCell>{t('companySettings.backup.filename')}</TableCell>
                <TableCell>{t('companySettings.backup.size')}</TableCell>
                <TableCell>{t('companySettings.backup.createdAt')}</TableCell>
                <TableCell align="center" sx={{ width: 150 }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(backups || []).map((backup) => (
                <TableRow key={backup.filename} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {backup.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={formatSize(backup.size)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{formatDate(backup.created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('companySettings.backup.restore')}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenRestore(backup.filename)}
                        disabled={isBusy}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.download')}>
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(backup.filename)}
                        disabled={isBusy}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteFile(backup.filename)}
                        disabled={isBusy}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload drop zone */}
      <Box
        data-testid="settings__backup__drop-zone"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        sx={{
          mt: 2,
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
          '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
        }}
      >
        {uploading ? (
          <LinearProgress sx={{ width: '60%', mx: 'auto' }} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t('companySettings.backup.uploadBackup')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {t('companySettings.backup.uploadHelp')}
            </Typography>
          </>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".sql"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* ============================================================= */}
      {/* Restore Dialog                                                 */}
      {/* ============================================================= */}
      <Dialog
        open={!!restoreFile}
        onClose={() => !restoring && setRestoreFile(null)}
        maxWidth="sm"
        fullWidth
        data-testid="settings__backup__dialog--restore"
      >
        {restoring && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {t('companySettings.backup.restore')}
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label={t('common.close')} size="small" onClick={() => setRestoreFile(null)} disabled={restoring}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('companySettings.backup.restoreConfirm')}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {restoreFile}
          </Typography>

          <Typography variant="body2" fontWeight={600}>
            {t('companySettings.backup.restoreBackupFirst')}
          </Typography>

          {/* Step indicator */}
          {restoring && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {restoreStep === 'backing-up'
                ? t('companySettings.backup.createBackup') + '...'
                : t('companySettings.backup.restore') + '...'
              }
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setRestoreFile(null)} disabled={restoring}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="settings__backup__button--restore-without"
            variant="outlined"
            color="warning"
            onClick={handleRestoreWithout}
            disabled={restoring}
          >
            {t('companySettings.backup.restoreWithoutBackup')}
          </Button>
          <Button
            data-testid="settings__backup__button--restore-with"
            variant="contained"
            onClick={handleRestoreWithBackup}
            disabled={restoring}
          >
            {t('companySettings.backup.restoreWithBackup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Delete Confirm                                                 */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={!!deleteFile}
        title={t('companySettings.backup.deleteBackup')}
        message={t('companySettings.backup.deleteConfirm')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteFile(null)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />
    </Box>
  );
}
